import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi } from './api';
import { Notification } from './components/Notification';
import { ConfirmModal } from './components/ConfirmModal';

interface BlogPost {
  id: number;
  slug: string;
  title: Record<string, string>;
  excerpt: Record<string, string>;
  content: Record<string, string>;
  coverImage: string | null;
  status: 'draft' | 'published' | 'archived';
  publishedAt: string | null;
  category: string | null;
  isFeatured: boolean;
  authorName: string | null;
  authorRole: string | null;
  createdAt: string;
  updatedAt: string;
}

const CATEGORY_OPTIONS = ['Новости', 'Фестивали', 'Советы', 'Истории'];

export const BlogAdmin: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [userRole, setUserRole] = useState<string>('');
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ show: boolean; id: number | null }>({ show: false, id: null });
  const navigate = useNavigate();

  useEffect(() => {
    const userStr = localStorage.getItem('admin_user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setUserRole(user.role || '');
      } catch {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get<{ posts: BlogPost[] }>('/api/admin/blog');
      setPosts(data.posts);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не удалось загрузить статьи');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id: number) => {
    setConfirmDelete({ show: true, id });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete.id) return;
    try {
      await adminApi.delete(`/api/admin/blog/${confirmDelete.id}`);
      setNotification({ message: 'Статья удалена', type: 'success' });
      fetchPosts();
    } catch (e: any) {
      setNotification({ message: e?.response?.data?.message || 'Ошибка удаления', type: 'error' });
    } finally {
      setConfirmDelete({ show: false, id: null });
    }
  };

  const statusLabel = (status: BlogPost['status']) => {
    if (status === 'published') return 'Опубликовано';
    if (status === 'draft') return 'Черновик';
    return 'Архив';
  };

  return (
    <div>
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="page-header flex items-center justify-between gap-4">
        <div>
          <h1 className="page-title">Блог</h1>
          <p className="page-subtitle">Управление статьями и публикациями</p>
        </div>
        {(userRole === 'admin' || userRole === 'manager') && (
          <button
            onClick={() => navigate('/admin/blog/new')}
            className="btn-primary"
          >
            Новая статья
          </button>
        )}
      </div>

      {error && <div className="alert alert-error mb-5">{error}</div>}

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="loading-spinner w-10 h-10"></div>
        </div>
      ) : (
        <div className="table-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="w-[35%]">Заголовок</th>
                <th className="w-[140px]">Категория</th>
                <th className="w-[130px]">Статус</th>
                <th className="w-[150px]">Опубликовано</th>
                <th className="w-[50px]"></th>
              </tr>
            </thead>
            <tbody>
              {posts.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <div className="empty-state">
                      <div className="empty-state-text">Статей ещё нет</div>
                    </div>
                  </td>
                </tr>
              ) : (
                posts.map(p => (
                  <tr
                    key={p.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/admin/blog/${p.id}/edit`)}
                  >
                    <td>
                      <div className="font-medium text-[#1890ff] flex items-center gap-2">
                        {p.title.ru || p.title.en || p.slug}
                        {p.isFeatured && (
                          <span className="text-[10px] font-semibold text-[#faad14] bg-[#fffbe6] border border-[#ffe58f] rounded px-1.5 py-0.5 uppercase tracking-wide">
                            Featured
                          </span>
                        )}
                      </div>
                      <div className="text-[13px] text-gray-500">{p.slug}</div>
                    </td>
                    <td className="text-sm text-gray-600">{p.category || '—'}</td>
                    <td>
                      <span className={`status-badge status-${p.status}`}>
                        {statusLabel(p.status)}
                      </span>
                    </td>
                    <td>{p.publishedAt ? new Date(p.publishedAt).toLocaleDateString('ru-RU') : '—'}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {(userRole === 'admin' || userRole === 'manager') && (
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(openMenuId === p.id ? null : p.id);
                            }}
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-transparent border-0 cursor-pointer text-[20px] text-black hover:bg-[#f5f5f5]"
                            aria-label="Действия"
                          >
                            ⋮
                          </button>
                          {openMenuId === p.id && (
                            <>
                              <div
                                className="fixed inset-0 z-[999] cursor-default"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuId(null);
                                }}
                              />
                              <div className="absolute top-full right-0 mt-1 bg-white border border-[#d9d9d9] rounded-lg shadow-lg z-[1000] min-w-[160px]">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuId(null);
                                    navigate(`/admin/blog/${p.id}/edit`);
                                  }}
                                  className="w-full px-4 py-2.5 text-left bg-transparent border-0 cursor-pointer text-sm text-black border-b border-[#f0f0f0] hover:bg-[#f5f5f5]"
                                >
                                  Редактировать
                                </button>
                                {userRole === 'admin' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenMenuId(null);
                                      handleDelete(p.id);
                                    }}
                                    className="w-full px-4 py-2.5 text-left bg-transparent border-0 cursor-pointer text-sm text-[#ff4d4f] hover:bg-[#fff2f0]"
                                  >
                                    Удалить
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDelete.show}
        title="Подтвердите удаление"
        message="Вы уверены, что хотите удалить статью? Это действие нельзя отменить."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete({ show: false, id: null })}
        confirmText="Удалить"
        cancelText="Отмена"
        isDanger={true}
      />
    </div>
  );
};

interface BlogFormData {
  slug: string;
  titleRu: string;
  titleKy: string;
  titleEn: string;
  excerptRu: string;
  excerptKy: string;
  excerptEn: string;
  contentRu: string;
  contentKy: string;
  contentEn: string;
  coverImage: string;
  status: 'draft' | 'published' | 'archived';
  category: string;
  isFeatured: boolean;
  authorName: string;
  authorRole: string;
}

const emptyForm: BlogFormData = {
  slug: '',
  titleRu: '',
  titleKy: '',
  titleEn: '',
  excerptRu: '',
  excerptKy: '',
  excerptEn: '',
  contentRu: '',
  contentKy: '',
  contentEn: '',
  coverImage: '',
  status: 'draft',
  category: '',
  isFeatured: false,
  authorName: 'Команда Жашыл Дем',
  authorRole: 'Редакция проекта'
};

export const BlogPostForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState<BlogFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await adminApi.get<{ post: BlogPost }>(`/api/admin/blog/${id}`);
        const p = data.post;
        setForm({
          slug: p.slug,
          titleRu: p.title.ru || '',
          titleKy: p.title.ky || '',
          titleEn: p.title.en || '',
          excerptRu: p.excerpt?.ru || '',
          excerptKy: p.excerpt?.ky || '',
          excerptEn: p.excerpt?.en || '',
          contentRu: p.content?.ru || '',
          contentKy: p.content?.ky || '',
          contentEn: p.content?.en || '',
          coverImage: p.coverImage || '',
          status: p.status,
          category: p.category || '',
          isFeatured: !!p.isFeatured,
          authorName: p.authorName || 'Команда Жашыл Дем',
          authorRole: p.authorRole || 'Редакция проекта'
        });
      } catch (e) {
        setError('Не удалось загрузить статью');
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  // Транслитерация RU → латиница (для slug автогенерации)
  const makeSlug = (text: string) => {
    const map: Record<string, string> = {
      а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo',
      ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm',
      н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u',
      ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch',
      ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
      ө: 'o', ү: 'u', ң: 'ng'
    };
    const t = text.toLowerCase().split('').map(c => map[c] !== undefined ? map[c] : c).join('');
    return t.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 100) || 'article';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload: any = {
      title: { ru: form.titleRu, ky: form.titleKy, en: form.titleEn },
      excerpt: { ru: form.excerptRu, ky: form.excerptKy, en: form.excerptEn },
      content: { ru: form.contentRu, ky: form.contentKy, en: form.contentEn },
      coverImage: form.coverImage.trim() || null,
      status: form.status,
      category: form.category.trim() || null,
      isFeatured: form.isFeatured,
      authorName: form.authorName.trim() || null,
      authorRole: form.authorRole.trim() || null
    };
    // При создании — автогенерация slug из русского заголовка
    if (!isEdit) {
      payload.slug = makeSlug(form.titleRu.trim());
    }
    try {
      if (isEdit) {
        await adminApi.put(`/api/admin/blog/${id}`, payload);
      } else {
        await adminApi.post('/api/admin/blog', payload);
      }
      navigate('/admin/blog');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const setField = <K extends keyof BlogFormData>(k: K, v: BlogFormData[K]) =>
    setForm(prev => ({ ...prev, [k]: v }));

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="loading-spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{isEdit ? 'Редактировать статью' : 'Новая статья'}</h1>
        <p className="page-subtitle">Многоязычная публикация — заголовок, аннотация и текст на трёх языках</p>
      </div>

      {error && <div className="alert alert-error mb-5">{error}</div>}

      <form onSubmit={handleSubmit} className="admin-form max-w-[1100px]">
        <div className="form-section">
          <h3 className="form-section-title">Заголовок</h3>
          <div className="form-grid form-grid-3">
            <div className="form-group">
              <label className="form-label">Русский *</label>
              <input
                type="text"
                className="form-input"
                value={form.titleRu}
                onChange={e => setField('titleRu', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Кыргызский</label>
              <input
                type="text"
                className="form-input"
                value={form.titleKy}
                onChange={e => setField('titleKy', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">English</label>
              <input
                type="text"
                className="form-input"
                value={form.titleEn}
                onChange={e => setField('titleEn', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3 className="form-section-title">Аннотация</h3>
          <div className="form-grid form-grid-3">
            <div className="form-group">
              <label className="form-label">Русский</label>
              <textarea
                className="form-input h-20"
                value={form.excerptRu}
                onChange={e => setField('excerptRu', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Кыргызский</label>
              <textarea
                className="form-input h-20"
                value={form.excerptKy}
                onChange={e => setField('excerptKy', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">English</label>
              <textarea
                className="form-input h-20"
                value={form.excerptEn}
                onChange={e => setField('excerptEn', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3 className="form-section-title">Обложка</h3>
          <div className="form-group">
            <label className="form-label">URL обложки (относительный или полный)</label>
            <input
              type="text"
              className="form-input"
              value={form.coverImage}
              onChange={e => setField('coverImage', e.target.value)}
              placeholder="/uploads/cover.jpg"
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="form-section-title">Контент</h3>
          <div className="form-grid form-grid-3">
            <div className="form-group">
              <label className="form-label">Русский</label>
              <textarea
                className="form-input h-64 font-mono text-xs"
                value={form.contentRu}
                onChange={e => setField('contentRu', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Кыргызский</label>
              <textarea
                className="form-input h-64 font-mono text-xs"
                value={form.contentKy}
                onChange={e => setField('contentKy', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">English</label>
              <textarea
                className="form-input h-64 font-mono text-xs"
                value={form.contentEn}
                onChange={e => setField('contentEn', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3 className="form-section-title">Категория и автор</h3>
          <div className="form-group">
            <label className="form-label">Категория</label>
            <select
              className="form-input"
              value={form.category}
              onChange={e => setField('category', e.target.value)}
            >
              <option value="">— без категории —</option>
              {CATEGORY_OPTIONS.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <label className="admin-checkbox-row">
            <input
              type="checkbox"
              className="admin-checkbox"
              checked={form.isFeatured}
              onChange={e => setField('isFeatured', e.target.checked)}
            />
            <div>
              <div className="admin-checkbox-title">Рекомендуемая статья</div>
              <div className="admin-checkbox-hint">Будет выделена в списке блога.</div>
            </div>
          </label>
          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label className="form-label">Автор — имя</label>
              <input
                type="text"
                className="form-input"
                value={form.authorName}
                onChange={e => setField('authorName', e.target.value)}
                placeholder="Команда Жашыл Дем"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Автор — должность</label>
              <input
                type="text"
                className="form-input"
                value={form.authorRole}
                onChange={e => setField('authorRole', e.target.value)}
                placeholder="Редакция проекта"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3 className="form-section-title">Статус</h3>
          <div className="form-group max-w-xs">
            <select
              className="form-input"
              value={form.status}
              onChange={e => setField('status', e.target.value as BlogFormData['status'])}
            >
              <option value="draft">Черновик</option>
              <option value="published">Опубликовано</option>
              <option value="archived">Архив</option>
            </select>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/admin/blog')}
            className="btn-secondary"
            disabled={saving}
          >
            Отмена
          </button>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
      </form>
    </div>
  );
};
